import { useEffect, useMemo, useRef, useState } from 'react';
import Drawer from '@app/components/Drawer';
import OrgModulePlaceholder from '../OrgModulePlaceholder';
import type {
  Device,
  DiscoveryEvent,
  DiscoveryEventFinishedData,
  DiscoveryEventProgressData,
  DiscoveryEventStatusData,
  DiscoveryJobschema,
  DiscoveryRun,
  DiscoveryRunSummaryschema
} from '@app/types/sdk';
import { formatDuration, formatNumber, formatRelativeTime } from '@app/utils/format';
import { describeDiscoverySchedule } from './helpers';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import { buildAccessTokenProvider } from '@app/api/token';
import { ForkIcon, LayersIcon, NetworkIcon, PauseIcon, RadarIcon, ShieldIcon, UserIcon } from '@app/components/icons';

interface DiscoveryRunDetailDrawerProps {
  open: boolean;
  orgId: string | null;
  job: DiscoveryJobschema | null;
  run: DiscoveryRun | null;
  siteNames: Record<string, string>;
  onClose: () => void;
  onStopRun?: () => void;
  stopInProgress?: boolean;
  onRefreshRuns: () => void;
  onRefreshJobs?: () => void;
}

interface EventStreamState {
  events: DiscoveryEvent[];
  streamStatus: 'idle' | 'connecting' | 'open' | 'error';
}

type DeviceTypeIconKind = 'endpoint' | 'firewall' | 'load-balancer' | 'router' | 'switch' | 'unknown' | 'wireless-ap';

interface DeviceTypeSummary {
  key: string;
  label: string;
  count: number;
  iconKind: DeviceTypeIconKind;
  detail?: string;
}

interface DeviceVendorSummary {
  key: string;
  label: string;
  count: number;
}

interface DiscoveredDeviceDetail {
  key: string;
  label: string;
  iconKind: DeviceTypeIconKind;
  mgmtAddress?: string;
  vendor?: string;
  model?: string;
  role?: string;
  typeLabel: string;
}

interface DiscoveryRunDeviceResult {
  deviceId?: string;
  siteId?: string;
  mgmtAddress?: string;
  hostname?: string;
  displayName?: string;
  vendor?: string;
  model?: string;
  osName?: string;
  osVersion?: string;
  version?: string;
  role?: string;
  state?: string;
  created?: boolean;
  updated?: boolean;
  observedAt?: string;
}

interface DiscoveryRunLinkResult {
  layer?: string;
  fromDeviceId?: string;
  fromInterface?: string;
  toDeviceId?: string;
  toInterface?: string;
  observedAt?: string;
}

interface DiscoveryRunResultsState {
  devices: DiscoveryRunDeviceResult[];
  links: DiscoveryRunLinkResult[];
  status: 'idle' | 'loading' | 'ready' | 'error';
}

interface PersistedRunEventDetail {
  key: string;
  badge: string;
  observedAt?: string;
  description: string;
}

const isRunLive = (status?: string | null) => status === 'running' || status === 'queued';
const isTerminalRunStatus = (status?: string | null) => {
  const normalized = (status ?? '').trim().toLowerCase();
  return normalized === 'succeeded' || normalized === 'failed' || normalized === 'partial' || normalized === 'cancelled' || normalized === 'canceled';
};
export const shouldClearDiscoveryRunEvents = (previousStreamKey: string, nextStreamKey: string): boolean => previousStreamKey !== nextStreamKey;
export const buildDiscoveryRunEventKey = (orgId: string | null, runId: string | undefined): string => [orgId ?? '', runId ?? ''].join(':');
const buildDiscoveryRunConnectionKey = (orgId: string | null, jobId: string | undefined, runId: string | undefined): string =>
  [orgId ?? '', jobId ?? '', runId ?? ''].join(':');
const SSE_REFRESH_BATCH_SIZE = 4;
const SSE_REFRESH_MIN_INTERVAL_MS = 5000;

const resolveEventType = (event: DiscoveryEvent | undefined) => {
  if (!event) {
    return undefined;
  }
  const payload = event.data as { type?: string } | undefined;
  return payload?.type ?? event.type;
};

const parseDiscoveryEventChunk = (chunk: string): { event?: string; data?: string } | null => {
  if (!chunk.trim()) {
    return null;
  }
  const lines = chunk.split(/\r?\n/);
  const bag: { event?: string; data?: string } = {};
  for (const line of lines) {
    if (line.startsWith('event:')) {
      bag.event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      bag.data = (bag.data ?? '') + line.slice(5).trim();
    }
  }
  return bag;
};

const mergeDiscoveryEvents = (previousEvents: DiscoveryEvent[], nextEvents: DiscoveryEvent[]): DiscoveryEvent[] => {
  const seen = new Set(previousEvents.map((event) => event.seq));
  const deduped: DiscoveryEvent[] = [];
  for (const event of nextEvents) {
    const seq = event?.seq;
    if (typeof seq === 'number') {
      if (seen.has(seq) || deduped.some((queued) => queued.seq === seq)) {
        continue;
      }
      seen.add(seq);
    }
    deduped.push(event);
  }
  return deduped.length === 0 ? previousEvents : [...previousEvents, ...deduped].slice(-200);
};

const toCleanString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toTitleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => (part.length <= 3 && part === part.toUpperCase() ? part : `${part.slice(0, 1).toUpperCase()}${part.slice(1).toLowerCase()}`))
    .join(' ');

const normalizeVendorLabel = (vendor: string | undefined): string | undefined => {
  if (!vendor) {
    return undefined;
  }
  const normalized = vendor.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!normalized) {
    return undefined;
  }
  if (normalized.includes('palo alto')) {
    return 'Palo Alto';
  }
  if (normalized.includes('juniper')) {
    return 'Juniper';
  }
  if (normalized.includes('cisco')) {
    return 'Cisco';
  }
  if (normalized.includes('extreme')) {
    return 'Extreme';
  }
  if (normalized.includes('arista')) {
    return 'Arista';
  }
  if (normalized.includes('fortinet')) {
    return 'Fortinet';
  }
  if (normalized.includes('f5')) {
    return 'F5';
  }
  return toTitleCase(normalized);
};

export const inferDeviceFamily = (payload: Record<string, unknown>): { family: string; iconKind: DeviceTypeIconKind } => {
  const explicit = toCleanString(payload.deviceType) ?? toCleanString(payload.deviceKind) ?? toCleanString(payload.kind) ?? toCleanString(payload.family);
  const role = toCleanString(payload.role);
  const platform = toCleanString(payload.platform);
  const model = toCleanString(payload.model);
  const vendor = toCleanString(payload.vendor);
  const hostname = toCleanString(payload.hostname);
  const displayName = toCleanString(payload.displayName);
  const haystack = [explicit, role, platform, model, vendor, hostname, displayName].filter(Boolean).join(' ').toLowerCase();
  const declaredType = [explicit, role].filter(Boolean).join(' ').toLowerCase();
  const hardwareHints = [platform, model].filter(Boolean).join(' ').toLowerCase();
  const switchPattern = /(switch|catalyst|nexus|c9\d{3}|c3\d{3}|ex\d{3}|qfx|dcs-|dcs\d|ceoslab|eos|(?:^|[\s_-])sw(?:[\s_-]|\d|$)|aruba[\s_-]?sw)/;
  const hasStrongSwitchHardwareSignal = switchPattern.test(hardwareHints);

  if (/(load[\s-]?balancer|\bf5\b|big[\s-]?ip|ltm|gtm)/.test(haystack)) {
    return { family: 'Load Balancer', iconKind: 'load-balancer' };
  }
  if (/(firewall|security gateway|palo alto|pan-os|\bpa-\d|asa\b|srx|fortigate|fortinet|checkpoint|check point)/.test(haystack)) {
    return { family: 'Firewall', iconKind: 'firewall' };
  }
  if (/(wireless[\s-]?ap|access[\s-]?point|\baruba[\s_-]?ap\b|(?:^|[\s_-])ap(?:[\s_-]|\d|$)|\bwlan\b)/.test(declaredType) && !hasStrongSwitchHardwareSignal) {
    return { family: 'Wireless AP', iconKind: 'wireless-ap' };
  }
  if (/(wireless[\s-]?ap|access[\s-]?point|\baruba[\s_-]?ap\b|(?:^|[\s_-])ap(?:[\s_-]|\d|$)|\bwlan\b|\bwi[\s-]?fi\b|\bwifi\b)/.test(haystack)) {
    if (switchPattern.test(haystack)) {
      return { family: 'Switch', iconKind: 'switch' };
    }
    return { family: 'Wireless AP', iconKind: 'wireless-ap' };
  }
  if (switchPattern.test(haystack)) {
    return { family: 'Switch', iconKind: 'switch' };
  }
  if (/(router|ios xr|ios-xr|isr|asr|mx\d|edge)/.test(haystack)) {
    return { family: 'Router', iconKind: 'router' };
  }
  if (/(endpoint|end system|server|host|workstation|client|linux|windows|macos)/.test(haystack)) {
    return { family: 'End System', iconKind: 'endpoint' };
  }
  return { family: 'Device', iconKind: 'unknown' };
};

const getObjectField = (record: Record<string, unknown>, key: string): unknown => {
  const value = record[key];
  if (value !== undefined && value !== null) {
    return value;
  }
  const attributes = record.attributes;
  if (attributes && typeof attributes === 'object') {
    return (attributes as Record<string, unknown>)[key];
  }
  return undefined;
};

const deviceToDiscoveryPayload = (device: Device): Record<string, unknown> => {
  const record = device as unknown as Record<string, unknown>;
  return {
    ...record,
    deviceId: record.id,
    deviceType: getObjectField(record, 'deviceType'),
    deviceKind: getObjectField(record, 'deviceKind'),
    family: getObjectField(record, 'family'),
    platform: getObjectField(record, 'platform'),
    role: record.role ?? getObjectField(record, 'role'),
    vendor: record.vendor ?? getObjectField(record, 'vendor'),
    model: record.model ?? getObjectField(record, 'model'),
    osName: record.osName ?? getObjectField(record, 'osName'),
    osVersion: record.osVersion ?? getObjectField(record, 'osVersion'),
    version: record.version ?? getObjectField(record, 'version'),
    hostname: record.hostname ?? getObjectField(record, 'hostname'),
    displayName: record.displayName ?? getObjectField(record, 'displayName'),
    mgmtAddress: record.mgmtAddress ?? getObjectField(record, 'mgmtAddress')
  };
};

const looksLikeDeviceName = (value: string, payload: Record<string, unknown>): boolean => {
  const normalized = value.toLowerCase();
  const identityValues = [payload.hostname, payload.displayName, payload.deviceId, payload.id]
    .map((item) => toCleanString(item)?.toLowerCase())
    .filter(Boolean);
  if (identityValues.includes(normalized)) {
    return true;
  }
  return /^[a-z0-9]+[-_](?:sw|switch|ap|fw|pan|core|dist|acc|edge|isp|lab)[-_a-z0-9]*$/.test(normalized);
};

const sanitizeDeviceModel = (value: unknown, payload: Record<string, unknown>): string | undefined => {
  const model = toCleanString(value);
  if (!model || looksLikeDeviceName(model, payload)) {
    return undefined;
  }
  return model;
};

const resolveDeviceDetailText = (payload: Record<string, unknown>): string | undefined => {
  return sanitizeDeviceModel(payload.model, payload);
};

const isCreatedRunDevice = (device: DiscoveryRunDeviceResult): boolean => device.created === true;

const isCreatedDevicePayload = (payload: Record<string, unknown>): boolean => payload.created !== false && payload.updated !== true;

const normalizeIdentityValue = (value: unknown): string | undefined => {
  const clean = toCleanString(value);
  return clean ? clean.toLowerCase() : undefined;
};

const collectDeviceIdentityKeys = (payload: Record<string, unknown>): string[] => {
  const candidates = [
    ['mgmt', payload.mgmtAddress],
    ['mgmt', payload.managementAddress],
    ['hostname', payload.hostname],
    ['name', payload.displayName],
    ['id', payload.deviceId],
    ['id', payload.id]
  ] as const;
  const keys: string[] = [];
  for (const [prefix, value] of candidates) {
    const normalized = normalizeIdentityValue(value);
    if (normalized) {
      keys.push(`${prefix}:${normalized}`);
    }
  }
  return keys.filter((key, index, list) => list.indexOf(key) === index);
};

const mergeDevicePayload = (
  payloads: Record<string, Record<string, unknown>>,
  identityIndex: Map<string, string>,
  fallbackPayloads: Array<Record<string, unknown>>,
  payload: Record<string, unknown>
) => {
  const identityKeys = collectDeviceIdentityKeys(payload);
  const existingKey = identityKeys.map((key) => identityIndex.get(key)).find(Boolean);
  const primaryKey = existingKey ?? identityKeys[0];

  if (!primaryKey) {
    fallbackPayloads.push(payload);
    return;
  }

  payloads[primaryKey] = {
    ...(payloads[primaryKey] ?? {}),
    ...payload
  };
  identityKeys.forEach((key) => identityIndex.set(key, primaryKey));
};

const collectEventDevicePayloads = (events: DiscoveryEvent[]) => {
  const devicesByKey: Record<string, Record<string, unknown>> = {};
  const identityIndex = new Map<string, string>();
  const fallbackPayloads: Array<Record<string, unknown>> = [];

  for (const event of events) {
    const eventType = resolveEventType(event);
    const payload = event.data as unknown as Record<string, unknown> | undefined;
    if (!payload) {
      continue;
    }
    if (eventType === 'deviceDiscovered' && isCreatedDevicePayload(payload)) {
      mergeDevicePayload(devicesByKey, identityIndex, fallbackPayloads, payload);
    }
  }

  return { devicesByKey, fallbackPayloads, identityIndex };
};

export const countDiscoveredDeviceEvents = (events: DiscoveryEvent[]): number => {
  const { devicesByKey, fallbackPayloads } = collectEventDevicePayloads(events);
  return Object.keys(devicesByKey).length + fallbackPayloads.length;
};

const resolveDeviceTypeSummaryMetadata = (payload: Record<string, unknown>): Omit<DeviceTypeSummary, 'count'> => {
  const vendorLabel = normalizeVendorLabel(toCleanString(payload.vendor));
  const detail = resolveDeviceDetailText(payload);
  const { family, iconKind } = inferDeviceFamily(payload);
  const explicitLabel = toCleanString(payload.deviceType) ?? toCleanString(payload.deviceKind) ?? toCleanString(payload.family);
  const normalizedExplicit = explicitLabel?.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const label =
    explicitLabel && normalizedExplicit && normalizedExplicit !== family.toLowerCase() && normalizedExplicit !== 'device'
      ? toTitleCase(explicitLabel)
      : vendorLabel
      ? `${vendorLabel} ${family}`
      : family;
  return {
    key: label.toLowerCase(),
    label,
    iconKind,
    detail
  };
};

const summarizeDeviceTypePayloads = (payloads: Array<Record<string, unknown>>): DeviceTypeSummary[] => {
  const buckets = new Map<string, DeviceTypeSummary>();
  for (const payload of payloads) {
    const { key, label, iconKind, detail } = resolveDeviceTypeSummaryMetadata(payload);
    const current = buckets.get(key) ?? { key, label, count: 0, iconKind, detail: undefined };
    current.count += 1;
    if (!current.detail && detail) {
      current.detail = detail;
    }
    buckets.set(key, current);
  }

  return [...buckets.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

const collectRunDiscoveredDevicePayloads = (events: DiscoveryEvent[], siteDevices: Device[]): Array<Record<string, unknown>> => {
  const { devicesByKey, fallbackPayloads, identityIndex } = collectEventDevicePayloads(events);

  for (const device of siteDevices) {
    const payload = deviceToDiscoveryPayload(device);
    const matchingIdentity = collectDeviceIdentityKeys(payload).some((key) => identityIndex.has(key));
    if (matchingIdentity) {
      mergeDevicePayload(devicesByKey, identityIndex, fallbackPayloads, payload);
    }
  }

  return [...Object.values(devicesByKey), ...fallbackPayloads];
};

const runResultDeviceToPayload = (device: DiscoveryRunDeviceResult): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    deviceId: device.deviceId,
    siteId: device.siteId,
    mgmtAddress: device.mgmtAddress,
    hostname: device.hostname,
    displayName: device.displayName,
    vendor: device.vendor,
    model: device.model,
    osName: device.osName,
    osVersion: device.osVersion,
    version: device.version,
    role: device.role,
    state: device.state,
    created: device.created,
    updated: device.updated
  };
  return {
    ...payload,
    model: sanitizeDeviceModel(payload.model, payload)
  };
};

const collectPersistedRunDevicePayloads = (devices: DiscoveryRunDeviceResult[]): Array<Record<string, unknown>> =>
  devices
    .filter(isCreatedRunDevice)
    .map(runResultDeviceToPayload)
    .filter((payload) => collectDeviceIdentityKeys(payload).length > 0 || toCleanString(payload.vendor) || toCleanString(payload.model));

const summarizeVendorPayloads = (payloads: Array<Record<string, unknown>>): DeviceVendorSummary[] => {
  const buckets = new Map<string, DeviceVendorSummary>();
  payloads.forEach((payload) => {
    const label = normalizeVendorLabel(toCleanString(payload.vendor));
    if (!label) {
      return;
    }
    const key = label.toLowerCase();
    const current = buckets.get(key) ?? { key, label, count: 0 };
    current.count += 1;
    buckets.set(key, current);
  });
  return [...buckets.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

export const selectDiscoveredDeviceTypePayloads = (
  discoveredCount: number,
  persistedPayloads: Array<Record<string, unknown>>,
  livePayloads: Array<Record<string, unknown>>
): Array<Record<string, unknown>> => {
  if (discoveredCount <= 0) {
    return [];
  }
  if (persistedPayloads.length > 0) {
    return persistedPayloads.slice(0, discoveredCount);
  }
  return livePayloads.slice(-discoveredCount);
};

export const resolveRunDiscoveredDeviceCount = (
  runIsTerminal: boolean,
  runResultsStatus: DiscoveryRunResultsState['status'],
  persistedCreatedCount: number,
  reportedDiscoveredCount: number | undefined,
  _eventDiscoveredCount: number
): number => {
  if (runIsTerminal && (runResultsStatus === 'loading' || runResultsStatus === 'ready')) {
    return persistedCreatedCount;
  }
  return reportedDiscoveredCount ?? 0;
};

export const shouldUsePersistedRunEventDetails = (
  runIsTerminal: boolean,
  runResultsStatus: DiscoveryRunResultsState['status'],
  persistedEventCount: number
): boolean => runIsTerminal && runResultsStatus === 'ready' && persistedEventCount > 0;

export const summarizeDiscoveredDeviceTypes = (events: DiscoveryEvent[], siteDevices: Device[]): DeviceTypeSummary[] =>
  summarizeDeviceTypePayloads(collectRunDiscoveredDevicePayloads(events, siteDevices));

export const listDiscoveredDevices = (events: DiscoveryEvent[], siteDevices: Device[]): DiscoveredDeviceDetail[] =>
  collectRunDiscoveredDevicePayloads(events, siteDevices)
    .map((payload, index) => {
      const { key, label: typeLabel, iconKind } = resolveDeviceTypeSummaryMetadata(payload);
      const deviceId = toCleanString(payload.deviceId) ?? toCleanString(payload.id);
      const mgmtAddress = toCleanString(payload.mgmtAddress) ?? toCleanString(payload.managementAddress);
      const label = toCleanString(payload.hostname) ?? toCleanString(payload.displayName) ?? deviceId ?? mgmtAddress ?? 'Discovered device';
      return {
        key: `${deviceId ?? mgmtAddress ?? key}:${index}`,
        label,
        iconKind,
        mgmtAddress,
        vendor: normalizeVendorLabel(toCleanString(payload.vendor)),
        model: sanitizeDeviceModel(payload.model, payload),
        role: toCleanString(payload.role),
        typeLabel
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

const listPersistedRunEventDetails = (
  devices: DiscoveryRunDeviceResult[],
  links: DiscoveryRunLinkResult[]
): PersistedRunEventDetail[] => {
  const deviceEvents = devices.map((device, index) => {
    const deviceId = toCleanString(device.deviceId);
    const label = toCleanString(device.hostname) ?? toCleanString(device.displayName) ?? deviceId ?? toCleanString(device.mgmtAddress) ?? 'Device';
    const devicePayload = runResultDeviceToPayload(device);
    const typeParts = [
      normalizeVendorLabel(toCleanString(device.vendor)),
      sanitizeDeviceModel(device.model, devicePayload),
      toCleanString(device.role)
    ].filter(Boolean);
    const address = toCleanString(device.mgmtAddress);
    const changeLabel = device.created && !device.updated ? 'discovered' : device.updated && !device.created ? 'updated' : 'discovered';
    return {
      key: `device:${deviceId ?? label}:${index}`,
      badge: device.updated && !device.created ? 'DEVICEUPDATED' : 'DEVICEDISCOVERED',
      observedAt: device.observedAt,
      description: `Device ${label}${address ? ` (${address})` : ''} ${changeLabel}${typeParts.length ? ` — ${typeParts.join(' · ')}` : ''}`
    };
  });
  const linkEvents = links.map((link, index) => ({
    key: `link:${link.fromDeviceId ?? 'from'}:${link.fromInterface ?? ''}:${link.toDeviceId ?? 'to'}:${link.toInterface ?? ''}:${index}`,
    badge: 'LINKDISCOVERED',
    observedAt: link.observedAt,
    description: `Link ${link.fromDeviceId}${link.fromInterface ? `:${link.fromInterface}` : ''} ⇄ ${link.toDeviceId}${
      link.toInterface ? `:${link.toInterface}` : ''
    }${link.layer ? ` (${link.layer.toUpperCase()})` : ''}`
  }));

  return [...deviceEvents, ...linkEvents].sort((a, b) => {
    const aTime = a.observedAt ? Date.parse(a.observedAt) : 0;
    const bTime = b.observedAt ? Date.parse(b.observedAt) : 0;
    return bTime - aTime;
  });
};

const renderDeviceTypeIcon = (iconKind: DeviceTypeIconKind) => {
  switch (iconKind) {
    case 'endpoint':
      return <UserIcon size={24} aria-hidden="true" />;
    case 'firewall':
      return <ShieldIcon size={24} aria-hidden="true" />;
    case 'load-balancer':
      return <ForkIcon size={24} aria-hidden="true" />;
    case 'router':
      return <NetworkIcon size={24} aria-hidden="true" />;
    case 'switch':
      return <LayersIcon size={24} aria-hidden="true" />;
    case 'wireless-ap':
      return <RadarIcon size={24} aria-hidden="true" />;
    default:
      return <RadarIcon size={24} aria-hidden="true" />;
  }
};

const parseEventDescription = (event: DiscoveryEvent): string => {
  const payload = event.data as DiscoveryEvent['data'] & Record<string, any>;
  const type = payload?.type ?? event.type;
  switch (type) {
    case 'status': {
      const statusPayload = payload as Partial<DiscoveryEventStatusData> & { new?: string };
      const from = statusPayload.old ?? 'queued';
      const to = statusPayload._new ?? statusPayload.new ?? 'unknown';
      return `Status ${from} → ${to}`;
    }
    case 'progress': {
      const progressPayload = payload as Partial<DiscoveryEventProgressData>;
      const percent = typeof progressPayload.percent === 'number' ? progressPayload.percent : 0;
      return `Progress ${percent}%`;
    }
    case 'deviceDiscovered':
      return `Device ${payload.deviceId} (${payload.mgmtAddress}) discovered`;
    case 'linkDiscovered':
      return `Link ${payload.fromDeviceId}:${payload.fromInterface} ⇄ ${payload.toDeviceId}:${payload.toInterface} (${payload.layer?.toUpperCase?.() ?? 'link'})`;
    case 'warning':
      return payload.message ?? 'Warning';
    case 'error':
      return payload.message ?? 'Error';
    case 'heartbeat':
      return 'Heartbeat';
    case 'finished':
      return `Run ${payload.status}`;
    default:
      return 'Event';
  }
};

export const useDiscoveryEventStream = (
  orgId: string | null,
  jobId: string | undefined,
  runId: string | undefined,
  connectionNonce: number,
  enabled: boolean,
  keepRetrying: boolean
): EventStreamState => {
  const { apiBaseUrl } = useConfig();
  const tokenResolver = useMemo(() => buildAccessTokenProvider(), []);
  const eventKey = useMemo(() => buildDiscoveryRunEventKey(orgId, runId), [orgId, runId]);
  const connectionKey = useMemo(() => buildDiscoveryRunConnectionKey(orgId, jobId, runId), [jobId, orgId, runId]);
  const activeConnectionKeyRef = useRef(connectionKey);
  const [eventState, setEventState] = useState<{ eventKey: string; events: DiscoveryEvent[] }>({ eventKey, events: [] });
  const [status, setStatus] = useState<EventStreamState['streamStatus']>('idle');

  activeConnectionKeyRef.current = connectionKey;
  const events = eventState.eventKey === eventKey ? eventState.events : [];

  useEffect(() => {
    setEventState((current) => (shouldClearDiscoveryRunEvents(current.eventKey, eventKey) ? { eventKey, events: [] } : current));
  }, [eventKey]);

  useEffect(() => {
    if (!enabled || !orgId || !jobId || !runId) {
      setStatus('idle');
      return;
    }

    const basePath = resolveBasePath(apiBaseUrl);
    const url = `${basePath}/orgs/${encodeURIComponent(orgId)}/discovery/jobs/${encodeURIComponent(jobId)}/runs/${encodeURIComponent(runId)}/events`;
    let cancelled = false;
    let reconnectHandle: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    const isCurrentStream = () => !cancelled && activeConnectionKeyRef.current === connectionKey;

    const scheduleReconnect = () => {
      if (!keepRetrying || !isCurrentStream()) {
        if (!keepRetrying && isCurrentStream()) {
          setStatus('idle');
        }
        return;
      }
      setStatus('connecting');
      reconnectHandle = setTimeout(() => {
        if (isCurrentStream()) {
          connect();
        }
      }, 2500);
    };

    const connect = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        if (isCurrentStream()) {
          setStatus('connecting');
        }
        const token = await tokenResolver();
        if (!isCurrentStream()) {
          return;
        }
        const response = await fetch(url, {
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: controller.signal
        });
        if (!response.ok || !response.body) {
          throw new Error(`Stream failed (${response.status})`);
        }
        if (isCurrentStream()) {
          setStatus('open');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        while (isCurrentStream()) {
          const { value, done } = await reader.read();
          if (done || !isCurrentStream()) {
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() ?? '';
          const nextEvents: DiscoveryEvent[] = [];
          for (const chunk of chunks) {
            const parsed = parseDiscoveryEventChunk(chunk);
            if (!parsed?.data) {
              continue;
            }
            try {
              const payload = JSON.parse(parsed.data) as DiscoveryEvent;
              nextEvents.push(payload);
            } catch {
              // ignore malformed events
            }
          }
          if (nextEvents.length > 0) {
            setEventState((prev) => {
              if (!isCurrentStream()) {
                return prev;
              }
              const previousEvents = prev.eventKey === eventKey ? prev.events : [];
              const mergedEvents = mergeDiscoveryEvents(previousEvents, nextEvents);
              return mergedEvents === previousEvents ? prev : { eventKey, events: mergedEvents };
            });
          }
        }
        scheduleReconnect();
      } catch {
        if (isCurrentStream()) {
          if (keepRetrying) {
            setStatus('connecting');
          } else {
            setStatus('error');
          }
        }
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      controller?.abort();
      if (reconnectHandle) {
        clearTimeout(reconnectHandle);
      }
    };
  }, [apiBaseUrl, connectionKey, connectionNonce, enabled, eventKey, jobId, keepRetrying, orgId, runId, tokenResolver]);

  return { events, streamStatus: status };
};

const useDiscoveryRunResults = (
  orgId: string | null,
  jobId: string | undefined,
  runId: string | undefined,
  enabled: boolean,
  refreshNonce: number
): DiscoveryRunResultsState => {
  const { apiBaseUrl } = useConfig();
  const tokenResolver = useMemo(() => buildAccessTokenProvider(), []);
  const resultKey = useMemo(() => buildDiscoveryRunConnectionKey(orgId, jobId, runId), [jobId, orgId, runId]);
  const [state, setState] = useState<DiscoveryRunResultsState>({ devices: [], links: [], status: 'idle' });

  useEffect(() => {
    if (!enabled || !orgId || !jobId || !runId) {
      setState({ devices: [], links: [], status: 'idle' });
      return;
    }

    const basePath = resolveBasePath(apiBaseUrl);
    const runPath = `${basePath}/orgs/${encodeURIComponent(orgId)}/discovery/jobs/${encodeURIComponent(jobId)}/runs/${encodeURIComponent(runId)}`;
    const controller = new AbortController();
    let cancelled = false;

    const loadResults = async () => {
      try {
        setState((current) => (current.status === 'loading' ? current : { devices: [], links: [], status: 'loading' }));
        const token = await tokenResolver();
        if (cancelled) {
          return;
        }
        const headers = {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
        const [devicesResponse, linksResponse] = await Promise.all([
          fetch(`${runPath}/devices`, { headers, signal: controller.signal }),
          fetch(`${runPath}/links`, { headers, signal: controller.signal })
        ]);
        if (!devicesResponse.ok) {
          throw new Error(`Run devices failed (${devicesResponse.status})`);
        }
        if (!linksResponse.ok) {
          throw new Error(`Run links failed (${linksResponse.status})`);
        }
        const devicesPayload = (await devicesResponse.json()) as { items?: DiscoveryRunDeviceResult[] };
        const linksPayload = (await linksResponse.json()) as { items?: DiscoveryRunLinkResult[] };
        if (!cancelled) {
          setState({
            devices: devicesPayload.items ?? [],
            links: linksPayload.items ?? [],
            status: 'ready'
          });
        }
      } catch {
        if (!cancelled) {
          setState({ devices: [], links: [], status: 'error' });
        }
      }
    };

    void loadResults();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [apiBaseUrl, enabled, jobId, orgId, refreshNonce, resultKey, runId, tokenResolver]);

  return state;
};

export const shouldStreamDiscoveryRun = (
  open: boolean,
  runId: string | undefined,
  status: string | null | undefined
) => open && Boolean(runId) && isRunLive(status);

const DiscoveryRunDetailDrawer = ({
  open,
  orgId,
  job,
  run,
  siteNames,
  onClose,
  onStopRun,
  stopInProgress = false,
  onRefreshRuns,
  onRefreshJobs
}: DiscoveryRunDetailDrawerProps) => {
  const [connectionNonce, setConnectionNonce] = useState(0);
  const [runResultsRefreshNonce, setRunResultsRefreshNonce] = useState(0);
  const processedEventCountRef = useRef(0);
  const pendingRefreshEventsRef = useRef(0);
  const lastRunRefreshAtRef = useRef(0);
  const lastTerminalResultsRefreshKeyRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const highlightTimerHandlesRef = useRef<Set<number>>(new Set());
  const previousTypeCountsRef = useRef<Map<string, number>>(new Map());
  const [highlightedTypeKeys, setHighlightedTypeKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    processedEventCountRef.current = 0;
    pendingRefreshEventsRef.current = 0;
    lastRunRefreshAtRef.current = 0;
    lastTerminalResultsRefreshKeyRef.current = null;
    previousTypeCountsRef.current = new Map();
    setHighlightedTypeKeys(new Set());
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    highlightTimerHandlesRef.current.forEach((handle) => window.clearTimeout(handle));
    highlightTimerHandlesRef.current.clear();
  }, [run?.id]);

  useEffect(
    () => () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      highlightTimerHandlesRef.current.forEach((handle) => window.clearTimeout(handle));
      highlightTimerHandlesRef.current.clear();
    },
    []
  );

  const runIsTerminal = isTerminalRunStatus(run?.status);
  const shouldStream = shouldStreamDiscoveryRun(open, run?.id, run?.status);
  const shouldKeepRetrying = open && Boolean(run?.id) && isRunLive(run?.status);

  useEffect(() => {
    if (shouldStream) {
      return;
    }
    processedEventCountRef.current = 0;
    pendingRefreshEventsRef.current = 0;
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, [shouldStream]);

  const { events, streamStatus } = useDiscoveryEventStream(orgId, job?.id, run?.id, connectionNonce, shouldStream, shouldKeepRetrying);
  const runResults = useDiscoveryRunResults(orgId, job?.id, run?.id, open && Boolean(run?.id), runResultsRefreshNonce);
  const detailEvents = events;

  useEffect(() => {
    if (!open || !run?.id || !runIsTerminal) {
      return;
    }
    const refreshKey = `${run.id}:${run.status ?? ''}:${run.finishedAt ?? ''}`;
    if (lastTerminalResultsRefreshKeyRef.current === refreshKey) {
      return;
    }
    lastTerminalResultsRefreshKeyRef.current = refreshKey;
    setRunResultsRefreshNonce((prev) => prev + 1);
  }, [open, run?.finishedAt, run?.id, run?.status, runIsTerminal]);

  useEffect(() => {
    if (!open || !run?.id || !isRunLive(run.status)) {
      return;
    }
    if (streamStatus !== 'error') {
      return;
    }
    const retryHandle = window.setTimeout(() => setConnectionNonce((prev) => prev + 1), 3000);
    return () => window.clearTimeout(retryHandle);
  }, [open, run?.id, run?.status, streamStatus]);

  const latestStatusEvent = useMemo(() => {
    for (let index = detailEvents.length - 1; index >= 0; index -= 1) {
      const event = detailEvents[index];
      if (resolveEventType(event) === 'status') {
        return event;
      }
    }
    return undefined;
  }, [detailEvents]);
  const latestProgressEvent = useMemo(() => {
    for (let index = detailEvents.length - 1; index >= 0; index -= 1) {
      const event = detailEvents[index];
      if (resolveEventType(event) === 'progress') {
        return event;
      }
    }
    return undefined;
  }, [detailEvents]);
  const latestFinishedEvent = useMemo(() => {
    for (let index = detailEvents.length - 1; index >= 0; index -= 1) {
      const event = detailEvents[index];
      if (resolveEventType(event) === 'finished') {
        return event;
      }
    }
    return undefined;
  }, [detailEvents]);
  useEffect(() => {
    if (!run?.id || !isRunLive(run.status)) {
      return;
    }
    if (events.length === 0) {
      return;
    }
    if (events.length < processedEventCountRef.current) {
      processedEventCountRef.current = 0;
    }
    const processed = processedEventCountRef.current;
    if (events.length <= processed) {
      return;
    }
    const newlyReceivedEvents = events.slice(processed);
    processedEventCountRef.current = events.length;
    if (newlyReceivedEvents.length === 0) {
      return;
    }

    const discoveredTypeKeys = [
      ...new Set(
        newlyReceivedEvents
          .filter((event) => resolveEventType(event) === 'deviceDiscovered')
          .map((event) => resolveDeviceTypeSummaryMetadata(event.data as unknown as Record<string, unknown>).key)
      )
    ];
    if (discoveredTypeKeys.length > 0) {
      setHighlightedTypeKeys((current) => {
        const next = new Set(current);
        discoveredTypeKeys.forEach((key) => next.add(key));
        return next;
      });
      const clearHandle = window.setTimeout(() => {
        highlightTimerHandlesRef.current.delete(clearHandle);
        setHighlightedTypeKeys((current) => {
          const next = new Set(current);
          discoveredTypeKeys.forEach((key) => next.delete(key));
          return next;
        });
      }, 2800);
      highlightTimerHandlesRef.current.add(clearHandle);
    }

    const hasTerminalUpdate = newlyReceivedEvents.some((event) => {
      const type = resolveEventType(event);
      if (type === 'finished') {
        return true;
      }
      if (type !== 'status') {
        return false;
      }
      const payload = event.data as (Partial<DiscoveryEventStatusData> & { new?: string; status?: string }) | undefined;
      const nextStatus = payload?._new ?? payload?.new ?? payload?.status;
      return isTerminalRunStatus(nextStatus);
    });

    if (hasTerminalUpdate) {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      pendingRefreshEventsRef.current = 0;
      lastRunRefreshAtRef.current = Date.now();
      setRunResultsRefreshNonce((prev) => prev + 1);
      onRefreshRuns();
      onRefreshJobs?.();
      return;
    }

    const meaningfulChanges = newlyReceivedEvents.filter((event) => {
      const type = resolveEventType(event);
      return type !== 'heartbeat' && type !== 'warning' && type !== 'error';
    }).length;
    if (meaningfulChanges === 0) {
      return;
    }

    pendingRefreshEventsRef.current += meaningfulChanges;
    if (pendingRefreshEventsRef.current < SSE_REFRESH_BATCH_SIZE) {
      return;
    }

    const triggerRunRefresh = () => {
      if (pendingRefreshEventsRef.current < SSE_REFRESH_BATCH_SIZE) {
        return;
      }
      pendingRefreshEventsRef.current = 0;
      lastRunRefreshAtRef.current = Date.now();
      onRefreshRuns();
    };

    const elapsed = Date.now() - lastRunRefreshAtRef.current;
    if (elapsed >= SSE_REFRESH_MIN_INTERVAL_MS) {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      triggerRunRefresh();
      return;
    }

    if (refreshTimerRef.current) {
      return;
    }

    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      triggerRunRefresh();
    }, SSE_REFRESH_MIN_INTERVAL_MS - elapsed);
  }, [events, onRefreshJobs, onRefreshRuns, run?.id, run?.status]);

  const visibleEvents = useMemo(
    () => detailEvents.filter((event) => {
      const type = resolveEventType(event);
      return type !== 'progress' && type !== 'status';
    }),
    [detailEvents]
  );
  const progressPercent = useMemo(() => {
    const percent = (latestProgressEvent?.data as Partial<DiscoveryEventProgressData> | undefined)?.percent;
    if (typeof percent !== 'number') {
      return runIsTerminal ? 100 : null;
    }
    return Math.max(0, Math.min(100, Math.round(percent)));
  }, [latestProgressEvent, runIsTerminal]);
  const discoveredDeviceEventCount = useMemo(() => countDiscoveredDeviceEvents(detailEvents), [detailEvents]);
  const persistedDevicePayloads = useMemo(() => collectPersistedRunDevicePayloads(runResults.devices), [runResults.devices]);
  const persistedDiscoveredDeviceCount = persistedDevicePayloads.length;

  const progressStats = useMemo(() => {
    const payload = latestProgressEvent?.data as Partial<DiscoveryEventProgressData> | undefined;
    const finishedPayload = latestFinishedEvent?.data as Partial<DiscoveryEventFinishedData> | undefined;
    const finishedSummary = finishedPayload?.summary as Partial<DiscoveryRunSummaryschema> | undefined;
    const summary = run?.summary as Partial<DiscoveryRunSummaryschema> | undefined;
    const reportedDevicesDiscovered = payload?.devicesDiscovered ?? finishedSummary?.devicesDiscovered ?? summary?.devicesDiscovered;
    return {
      devicesDiscovered: resolveRunDiscoveredDeviceCount(
        runIsTerminal,
        runResults.status,
        persistedDiscoveredDeviceCount,
        reportedDevicesDiscovered,
        discoveredDeviceEventCount
      ),
      devicesUpdated: payload?.devicesUpdated ?? finishedSummary?.devicesUpdated ?? summary?.devicesUpdated ?? 0,
      devicesUnreachable: payload?.devicesUnreachable ?? finishedSummary?.devicesUnreachable ?? summary?.devicesUnreachable ?? 0,
      linksDiscovered: payload?.linksDiscovered ?? finishedSummary?.linksDiscovered ?? summary?.linksDiscovered ?? 0
    };
  }, [discoveredDeviceEventCount, latestFinishedEvent, latestProgressEvent, persistedDiscoveredDeviceCount, run?.summary, runIsTerminal, runResults.status]);

  const liveDevicePayloads = useMemo(() => collectRunDiscoveredDevicePayloads(detailEvents, []), [detailEvents]);
  const deviceTypePayloads = useMemo(
    () => selectDiscoveredDeviceTypePayloads(progressStats.devicesDiscovered, persistedDevicePayloads, liveDevicePayloads),
    [liveDevicePayloads, persistedDevicePayloads, progressStats.devicesDiscovered]
  );
  const deviceTypeSummaries = useMemo(() => summarizeDeviceTypePayloads(deviceTypePayloads), [deviceTypePayloads]);
  const vendorSummaries = useMemo(() => summarizeVendorPayloads(deviceTypePayloads), [deviceTypePayloads]);
  const persistedRunEventDetails = useMemo(
    () => listPersistedRunEventDetails(runResults.devices, runResults.links),
    [runResults.devices, runResults.links]
  );
  const usePersistedEventDetails = shouldUsePersistedRunEventDetails(runIsTerminal, runResults.status, persistedRunEventDetails.length);
  const eventDetails = usePersistedEventDetails ? persistedRunEventDetails : [];
  const liveEventsForDisplay = usePersistedEventDetails ? [] : visibleEvents;
  const deviceTypeTotalCount = useMemo(
    () => deviceTypeSummaries.reduce((total, item) => total + item.count, 0),
    [deviceTypeSummaries]
  );

  useEffect(() => {
    const previous = previousTypeCountsRef.current;
    const changedKeys = deviceTypeSummaries
      .filter((item) => (previous.has(item.key) && previous.get(item.key) !== item.count) || (previous.size > 0 && !previous.has(item.key)))
      .map((item) => item.key);
    previousTypeCountsRef.current = new Map(deviceTypeSummaries.map((item) => [item.key, item.count]));

    if (changedKeys.length === 0) {
      return;
    }

    setHighlightedTypeKeys((current) => {
      const next = new Set(current);
      changedKeys.forEach((key) => next.add(key));
      return next;
    });

    const clearHandle = window.setTimeout(() => {
      setHighlightedTypeKeys((current) => {
        const next = new Set(current);
        changedKeys.forEach((key) => next.delete(key));
        return next;
      });
    }, 2800);

    return () => window.clearTimeout(clearHandle);
  }, [deviceTypeSummaries]);

  const statusLabel = useMemo(() => {
    const finishedPayload = latestFinishedEvent?.data as (Partial<DiscoveryEventFinishedData> & { status?: string }) | undefined;
    if (finishedPayload?.status) {
      return finishedPayload.status;
    }
    const payload = latestStatusEvent?.data as (Partial<DiscoveryEventStatusData> & { new?: string }) | undefined;
    const next = payload?._new ?? payload?.new;
    return next ?? run?.status ?? 'unknown';
  }, [latestFinishedEvent, latestStatusEvent, run?.status]);
  const currentRunIsLive = isRunLive(statusLabel.toLowerCase()) || isRunLive(run?.status);
  const eventStreamLabel =
    runIsTerminal || isTerminalRunStatus(statusLabel)
      ? 'run complete'
      : streamStatus === 'open'
      ? currentRunIsLive
        ? 'stream connected'
        : 'run complete'
      : streamStatus === 'connecting'
      ? 'connecting'
      : streamStatus === 'error'
      ? 'stream failed'
      : 'idle';
  const renderDeviceTypeSummary = () => {
    if (deviceTypeSummaries.length === 0) {
      return null;
    }

    return (
      <section className="discovery-device-types" aria-label="Devices by type">
        <header className="discovery-device-types__header">
          <h3>Devices by type</h3>
          <span>{formatNumber(deviceTypeTotalCount)} devices</span>
        </header>
        <div className="discovery-device-types__grid">
          {deviceTypeSummaries.map((item) => (
            <div
              key={item.key}
              className={['discovery-device-type', !runIsTerminal && highlightedTypeKeys.has(item.key) ? 'is-highlighted' : ''].filter(Boolean).join(' ')}
            >
              <div className="discovery-device-type__icon">{renderDeviceTypeIcon(item.iconKind)}</div>
              <div className="discovery-device-type__body">
                <p title={item.label}>{item.label}</p>
                {item.detail && <span>{item.detail}</span>}
              </div>
              <strong>{formatNumber(item.count)}</strong>
            </div>
          ))}
        </div>
        {vendorSummaries.length > 0 && (
          <div className="device-vendor-summary" aria-label="Devices by vendor">
            <span className="device-vendor-summary__label">Vendors</span>
            <div className="device-vendor-summary__chips">
              {vendorSummaries.map((item) => (
                <span key={item.key} className="device-vendor-chip">
                  {item.label} <strong>{formatNumber(item.count)}</strong>
                </span>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <Drawer
      open={open}
      title={job ? `Run for ${siteNames[job.siteId ?? ''] ?? job.siteId ?? 'site'}` : 'Run details'}
      subtitle={run ? `Started ${formatRelativeTime(run.startedAt ?? run.queuedAt)}` : undefined}
      onClose={onClose}
      width="wide"
      footer={
        <button type="button" className="ui-button ui-button--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {!run ? (
        <OrgModulePlaceholder intent="Select a run to view its status and live stream." />
      ) : (
        <>
          <div className="record-summary">
            <div className="record-summary__grid">
              <div>
                <span>Status</span>
                <strong>{statusLabel}</strong>
              </div>
              <div>
                <span>Duration</span>
                <strong>{formatDuration(run.startedAt ?? run.queuedAt, run.finishedAt ?? null)}</strong>
              </div>
              <div>
                <span>Schedule</span>
                <strong>{describeDiscoverySchedule(job?.schedule)}</strong>
              </div>
              <div>
                <span>Seeds</span>
                <strong className="record-summary__multiline">{job?.seeds?.length ? job.seeds.join(', ') : 'Operators supply seeds on run'}</strong>
              </div>
              <div>
                <span>Target CIDRs</span>
                <strong className="record-summary__multiline">{job?.targetCidrs?.length ? job.targetCidrs.join(', ') : 'Site defaults'}</strong>
              </div>
            </div>
          </div>
          <div className="discovery-run-details">
            <header className="discovery-run-details__progress-header">
              <p>{typeof progressPercent === 'number' ? `${progressPercent}% complete` : 'Progress pending'}</p>
              <div className="discovery-events__actions">
                <button
                  type="button"
                  className="ui-button ui-button--ghost"
                  onClick={() => {
                    setRunResultsRefreshNonce((prev) => prev + 1);
                    onRefreshRuns();
                  }}
                >
                  Refresh runs
                </button>
                {isRunLive(run?.status) && onStopRun && (
                  <button type="button" className="ui-button ui-button--danger" onClick={onStopRun} disabled={stopInProgress}>
                    <PauseIcon size={16} />
                    {stopInProgress ? 'Stopping…' : 'Stop run'}
                  </button>
                )}
              </div>
            </header>
            {typeof progressPercent === 'number' && (
              <div className="discovery-events__progress">
                <div className="discovery-events__progress-track">
                  <div className="discovery-events__progress-fill" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            )}
            <div className="discovery-events__metrics-grid">
              <div className="discovery-events__metric">
                <strong>{formatNumber(progressStats.devicesDiscovered)}</strong>
                <p>Devices discovered</p>
              </div>
              <div className="discovery-events__metric">
                <strong>{formatNumber(progressStats.devicesUpdated)}</strong>
                <p>Devices updated</p>
              </div>
              <div className="discovery-events__metric">
                <strong>{formatNumber(progressStats.devicesUnreachable)}</strong>
                <p>Unreachable</p>
              </div>
              <div className="discovery-events__metric">
                <strong>{formatNumber(progressStats.linksDiscovered)}</strong>
                <p>Links discovered</p>
              </div>
            </div>
            {renderDeviceTypeSummary()}
            <section className="discovery-events" aria-label="Live events">
              <header className="discovery-events__header">
                <p className="discovery-events__status">
                  Live events
                  <span aria-hidden="true"> · </span>
                  <span>{eventStreamLabel}</span>
                </p>
              </header>
              {liveEventsForDisplay.length > 0 ? (
                <ul className="discovery-events__list">
                  {liveEventsForDisplay.map((event) => (
                    <li key={event.seq} className="discovery-events__item">
                      <p className="discovery-events__item-type">
                        <span className="ui-badge ui-badge--ghost">{event.data.type}</span>
                        <span>{formatRelativeTime(event.time)}</span>
                      </p>
                      <p>{parseEventDescription(event)}</p>
                    </li>
                  ))}
                </ul>
              ) : eventDetails.length > 0 ? (
                <ul className="discovery-events__list">
                  {eventDetails.map((event) => (
                    <li key={event.key} className="discovery-events__item">
                      <p className="discovery-events__item-type">
                        <span className="ui-badge ui-badge--ghost">{event.badge}</span>
                        {event.observedAt && <span>{formatRelativeTime(event.observedAt)}</span>}
                      </p>
                      <p>{event.description}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="discovery-events__empty">
                  {runIsTerminal ? 'No saved discovery events are available for this completed run.' : 'Events will stream here once the backend emits SSE payloads.'}
                </p>
              )}
            </section>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default DiscoveryRunDetailDrawer;
